/**
 * `aria-rowindex` in the SAME coordinate space as `aria-rowcount`.
 *
 *   page 2, pageSize 25, server total 8,000, one row pinned to each end
 *
 *   index  1      column header
 *          2      pinned TOP      ─┐ every page paints these, so they are
 *          28..29 the page window  │ the FIRST and LAST rows of the table,
 *          8,003  pinned BOTTOM   ─┘ not neighbours of the page window
 *          ──────
 *          8,003  aria-rowcount   ── the bottom row's index IS the count
 *
 * Under `paginationMode="server"` MUI holds exactly one page of rows, and
 * `gridPaginationRowRangeSelector` returns `null`, so the row index it hands
 * `useGridRowAriaAttributes` restarts at 0 on every page. Reporting the server
 * total in `aria-rowcount` without moving the index makes the two contradict
 * each other.
 *
 * WAI-ARIA 1.2, `aria-rowindex`: "Authors MUST set the value for
 * aria-rowindex to an integer greater than or equal to 1, GREATER THAN THE
 * aria-rowindex VALUE OF ANY PREVIOUS ROWS, and less than or equal to the
 * number of rows in the full table."
 * https://www.w3.org/TR/wai-aria-1.2/#aria-rowindex
 *
 * All three clauses bind the pinned rows too — they are `role="row"` elements
 * inside the same grid. A bottom-pinned row that keeps its page-local index
 * renders AFTER row 29 while reporting 5, which breaks the middle clause.
 */

import { gridPinnedRowsSelector } from '@mui/x-data-grid/internals';
import {
  GridRow,
  type GridRowProps,
  gridColumnGroupsHeaderMaxDepthSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { createContext, forwardRef, useContext } from 'react';
import { ARIA_ROW_COUNT_UNKNOWN } from './ariaRowCount';

/** MUI's own offset: 1 for the column-header row, 1 because `aria-rowindex` is 1-based. */
const MUI_ROW_INDEX_BASE = 2;

/** Which block of the table a rendered row belongs to, in DOM order. */
type PagedRowSection = 'pinnedTop' | 'page' | 'pinnedBottom';

type PagedRowIndexFrame = {
  /** Rows before this page — `page * pageSize`, and 0 on the first page and while exporting. */
  offset: number;
  /** The grid's own `aria-rowcount`; `-1` while the server total is unknown. */
  ariaRowCount: number;
};

const PagedRowIndexFrameContext = createContext<PagedRowIndexFrame>({
  offset: 0,
  ariaRowCount: ARIA_ROW_COUNT_UNKNOWN,
});

export const PagedRowIndexFrameProvider = PagedRowIndexFrameContext.Provider;

export type PagedAriaRowIndexInput = PagedRowIndexFrame & {
  /** MUI's index of the row within the rendered page, pinned rows included. */
  index: number;
  headerRowDepth: number;
  section: PagedRowSection;
  /** 0-based place within the pinned BOTTOM block; ignored for the other sections. */
  pinnedBottomPosition: number;
  pinnedBottomCount: number;
};

const pagedAriaRowIndex = ({
  index,
  headerRowDepth,
  offset,
  ariaRowCount,
  section,
  pinnedBottomPosition,
  pinnedBottomCount,
}: PagedAriaRowIndexInput): number => {
  const withinPage = index + headerRowDepth + MUI_ROW_INDEX_BASE;

  // Pinned to the top: rows 1..n of the whole table, identical on every page,
  // and MUI already numbers them from 0 — so the page offset must NOT apply.
  if (section === 'pinnedTop') {
    return withinPage;
  }

  // Pinned to the bottom: the last rows of the whole table, counted back from
  // the total. With no total there is nothing to count back from, so they trail
  // the page window instead — still greater than every row above them.
  if (section === 'pinnedBottom' && ariaRowCount !== ARIA_ROW_COUNT_UNKNOWN) {
    return ariaRowCount - pinnedBottomCount + 1 + pinnedBottomPosition;
  }

  return withinPage + offset;
};

/**
 * The `row` slot for `mode="paged"`. `GridRow` spreads its own `aria-rowindex`
 * BEFORE the rest of its props (`GridRow.js:287`), so this one wins.
 */
export const PagedGridRow = forwardRef<HTMLDivElement, GridRowProps>(function PagedGridRow(props, ref) {
  const apiRef = useGridApiContext();
  const headerRowDepth = useGridSelector(apiRef, gridColumnGroupsHeaderMaxDepthSelector);
  const pinnedRows = useGridSelector(apiRef, gridPinnedRowsSelector);
  const { offset, ariaRowCount } = useContext(PagedRowIndexFrameContext);

  const pinnedBottomPosition = pinnedRows.bottom.findIndex((entry) => entry.id === props.rowId);
  const pinnedTop = pinnedRows.top.some((entry) => entry.id === props.rowId);
  const section: PagedRowSection = pinnedTop ? 'pinnedTop' : pinnedBottomPosition >= 0 ? 'pinnedBottom' : 'page';

  return (
    <GridRow
      ref={ref}
      {...props}
      aria-rowindex={pagedAriaRowIndex({
        index: props.index,
        headerRowDepth,
        offset,
        ariaRowCount,
        section,
        pinnedBottomPosition: Math.max(pinnedBottomPosition, 0),
        pinnedBottomCount: pinnedRows.bottom.length,
      })}
    />
  );
});
